import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Copy,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, ApiError } from '../../lib/api'
import {
  DocumentTemplateCategoryLabel,
  type DocumentTemplateCategoryT,
  type DocumentTemplateDto,
  type DocumentTemplateListResponse,
  type TemplateVariableCatalogItem,
} from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'

const STARTER_TEMPLATE = `# {{causa.title}}

Excmo. Sr.:

D./Dña. **{{cliente.displayName}}**, con DNI/CIF {{cliente.nationalId}},
representado por el Procurador {{procurador.fullName}} y bajo mi
dirección letrada, comparece y como mejor proceda en Derecho

**EXPONE**

PRIMERO.- Que en el procedimiento {{causa.courtFileNumber}} del
{{causa.courtName}} en relación con el expediente {{causa.code}}, vengo a
formular el presente escrito conforme a los siguientes hechos.

SEGUNDO.- (Hechos)

**FUNDAMENTOS DE DERECHO**

Por todo lo expuesto,

**SUPLICO** que, teniendo por presentado este escrito, se sirva
admitirlo y, en su mérito, acuerde lo procedente en Derecho.

{{fecha.ciudadFecha}}.

Fdo. (abogado)
`

export default function TemplatesSettingsPage() {
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [items, setItems] = useState<DocumentTemplateDto[]>([])
  const [catalog, setCatalog] = useState<TemplateVariableCatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Estado del editor
  const [name, setName] = useState('')
  const [category, setCategory] = useState<DocumentTemplateCategoryT>('ESCRITO')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState(STARTER_TEMPLATE)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<string>('')
  const [previewLoading, setPreviewLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<DocumentTemplateListResponse>('/document-templates')
      setItems(resp.items)
      setCatalog(resp.variableCatalog)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId]
  )

  // Cuando seleccionamos una plantilla, rellena el editor.
  useEffect(() => {
    if (selected) {
      setName(selected.name)
      setCategory(selected.category)
      setDescription(selected.description ?? '')
      setBody(selected.body)
    }
  }, [selected])

  // Refresca preview cada vez que cambia el body.
  useEffect(() => {
    const t = window.setTimeout(async () => {
      setPreviewLoading(true)
      try {
        const r = await api.post<{ rendered: string }>(
          `/document-templates/${selectedId ?? 'preview'}/preview`,
          { body }
        )
        setPreview(r.rendered)
      } catch {
        setPreview(body) // fallback: muestra sin sustituir
      } finally {
        setPreviewLoading(false)
      }
    }, 400)
    return () => window.clearTimeout(t)
  }, [body, selectedId])

  const onNew = () => {
    setSelectedId(null)
    setName('')
    setCategory('ESCRITO')
    setDescription('')
    setBody(STARTER_TEMPLATE)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !body.trim()) {
      setError('Indica nombre y cuerpo')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        category,
        description: description.trim() || null,
        body,
      }
      if (selectedId) {
        const r = await api.patch<{ template: DocumentTemplateDto }>(
          `/document-templates/${selectedId}`,
          payload
        )
        setSelectedId(r.template.id)
      } else {
        const r = await api.post<{ template: DocumentTemplateDto }>(
          '/document-templates',
          payload
        )
        setSelectedId(r.template.id)
      }
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!selectedId) return
    if (!confirm('¿Eliminar esta plantilla?')) return
    try {
      await api.delete(`/document-templates/${selectedId}`)
      setSelectedId(null)
      onNew()
      await load()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const copyVar = async (key: string) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`)
    } catch {
      /* ignore */
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">Plantillas de escritos</h2>
          <p className="t-muted text-sm mt-1">
            Plantillas reutilizables en formato Markdown con variables que se sustituyen al
            aplicarlas a un expediente.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={onNew}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nueva plantilla
          </button>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-[240px_1fr_240px] gap-4 min-h-[600px]">
        {/* Lista de plantillas */}
        <aside className="surface p-2 overflow-y-auto max-h-[700px]">
          {loading ? (
            <div className="t-faint text-xs flex items-center gap-1.5 py-2">
              <Loader2 size={11} className="animate-spin" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs t-faint p-2">Aún no hay plantillas.</p>
          ) : (
            <ul className="space-y-0.5">
              {items.map((t) => {
                const active = t.id === selectedId
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      className={[
                        'w-full text-left px-2 py-1.5 rounded text-sm',
                        active
                          ? 'bg-toga-500/15 text-toga-200 border border-toga-500/30'
                          : 't-secondary hover:bg-hover border border-transparent',
                      ].join(' ')}
                    >
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-[10px] t-faint">
                        {DocumentTemplateCategoryLabel[t.category]}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <form onSubmit={onSubmit} className="surface p-4 space-y-3 flex flex-col">
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre de la plantilla"
              className={`${input} font-semibold`}
              required
              disabled={!canWrite}
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentTemplateCategoryT)}
              className={input}
              disabled={!canWrite}
            >
              {Object.entries(DocumentTemplateCategoryLabel).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripción breve (opcional)"
            className={input}
            disabled={!canWrite}
          />

          <div className="grid grid-cols-2 gap-2 flex-1 min-h-[400px]">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={`${input} font-mono text-xs whitespace-pre`}
              spellCheck={false}
              disabled={!canWrite}
              style={{ minHeight: 400 }}
            />
            <div className="border border-base rounded p-3 overflow-y-auto bg-slate-900/30 text-sm">
              <div className="text-[10px] uppercase tracking-wider t-faint mb-2 flex items-center gap-2">
                Vista previa (sin expediente)
                {previewLoading && <Loader2 size={10} className="animate-spin" />}
              </div>
              <div className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview}</ReactMarkdown>
              </div>
            </div>
          </div>

          {canWrite && (
            <div className="flex justify-between items-center pt-2">
              {selectedId ? (
                <button
                  type="button"
                  onClick={() => void onDelete()}
                  className="px-3 py-1.5 rounded-lg text-sm text-rose-300 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/15 flex items-center gap-1.5"
                >
                  <Trash2 size={13} /> Eliminar
                </button>
              ) : (
                <span />
              )}
              <button
                type="submit"
                disabled={saving}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {selectedId ? 'Guardar cambios' : 'Crear plantilla'}
              </button>
            </div>
          )}
        </form>

        {/* Catálogo de variables */}
        <aside className="surface p-3 overflow-y-auto max-h-[700px]">
          <h3 className="text-sm font-semibold t-primary mb-2 flex items-center gap-2">
            <FileText size={14} className="text-toga-300" /> Variables
          </h3>
          <p className="text-[10px] t-faint mb-2">
            Click en una para copiar al portapapeles. Pega como {`{{...}}`} en el cuerpo.
          </p>
          <ul className="space-y-0.5 text-xs">
            {catalog.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  onClick={() => void copyVar(v.key)}
                  className="w-full text-left px-2 py-1 rounded hover:bg-hover flex items-center gap-1.5"
                  title={v.description}
                >
                  <Copy size={10} className="t-faint shrink-0" />
                  <span className="font-mono text-toga-300 truncate">
                    {`{{${v.key}}}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  )
}
